/* @flow */

import { normalizeUser, normalizeUsers } from '@braindate/domain/lib/user/util';

import * as namespaces from './userActionNamespaces';
import * as types from './userActionTypes';
import { patchEntities } from 'src/shared/domain/base/action/entityActions';
import {
  userActivityEndpoint,
  userEndpoint,
  userStatisticsEndpoint,
  userCanCreateTopicEndpoint,
  usersEndpoint,
  userAvatarEndpoint,
  userRecommendationUserEndpoint,
  userRecommendationTopicEndpoint,
} from 'src/shared/app/base/settings/endpointSettings';
import { parseResponse } from 'src/shared/app/base/util/ajaxUtils';

import type { UserActivity, User } from '@braindate/domain/lib/user/type';
import {
  setNewTopicReady,
  unsetNewTopicReady,
} from 'src/shared/app/topic/new/action/appNewTopicActions';
import type { GetState } from 'src/shared/core/type/reduxTypes';

// All users

export function simulateUsersFetch(namespace?: string) {
  return {
    type: types.SIMULATE_USERS_FETCH,
    namespace,
  };
}

export function fetchUsers(
  eventId: number,
  params?: Object,
  namespace?: string,
) {
  return (dispatch: any, _: GetState, { get }: Object) => {
    dispatch(fetchUsersRequest(namespace));
    type JSON = {
      count: number,
      next: string,
      previous: string,
      results: Array<Object>,
    };
    return get(usersEndpoint(), params)
      .then(parseResponse)
      .then((json: JSON) => {
        const { count, next, previous, results } = json;
        const normalizedData = normalizeUsers(results);
        const { entities, result } = normalizedData;

        dispatch(patchEntities(entities));
        dispatch(
          // $FlowIssue
          fetchUsersSuccess(result, count, next, previous, namespace, params),
        );
      })
      .catch((e) => {
        dispatch(fetchUsersFailure(e, namespace));

        throw e;
      });
  };
}

export function fetchUsersRequest(namespace?: string) {
  return {
    type: types.FETCH_USERS_REQUEST,
    namespace,
  };
}

export function fetchUsersSuccess(
  result: Array<User>,
  count?: number,
  next?: string,
  previous?: string,
  namespace?: string,
  params?: Object,
) {
  return {
    type: types.FETCH_USERS_SUCCESS,
    result,
    count,
    next,
    previous,
    namespace,
    params,
  };
}

export function fetchUsersFailure(exception: Error, namespace?: string) {
  return {
    type: types.FETCH_USERS_FAILURE,
    exception,
    namespace,
  };
}

export function resetUsers(namespace?: string) {
  return {
    type: types.RESET_USERS,
    namespace,
  };
}

// Single user

export function fetchUser(
  id: number | 'me',
  eventId: number | string,
  options?: Object,
  namespace?: string,
) {
  return (dispatch: any, _: GetState, { get }: Object) => {
    dispatch(fetchUserRequest(id, namespace));

    const params = {};

    if (options && typeof options === 'object') {
      if (options.optimized) {
        params.light = true;
      }

      if (options.tags) {
        params.annotate_keywords_state = true;
      }
    }

    return get(userEndpoint(id, eventId), params)
      .then(parseResponse)
      .then((json) => {
        const normalizedData = normalizeUser(json);
        const { entities, result } = normalizedData;

        dispatch(patchEntities(entities));
        dispatch(fetchUserSuccess(result, id, namespace));

        return json;
      })
      .catch((e) => {
        dispatch(fetchUserFailure(e, id, namespace));

        throw e;
      });
  };
}

export function fetchUserRequest(userId: number | 'me', namespace?: string) {
  return {
    type: types.FETCH_USER_REQUEST,
    namespace,
    userId,
  };
}

export function fetchUserSuccess(
  result: number,
  userId: number | 'me',
  namespace?: string,
) {
  return {
    type: types.FETCH_USER_SUCCESS,
    result,
    namespace,
    userId,
  };
}

export function fetchUserFailure(
  exception: Error,
  userId: number | 'me',
  namespace?: string,
) {
  return {
    type: types.FETCH_USER_FAILURE,
    exception,
    namespace,
    userId,
  };
}

// lazy fetch

export function lazyFetchAllUsers(url: string, params?: Object) {
  const baseParams = {
    random_order: true,
    people_tab_optin: true,
    visible: true,
    for_marketplace: 1,
    extended_search: true,
  };

  const obj =
    params && typeof params === 'object'
      ? {
          ...params,
          ...baseParams,
        }
      : baseParams;

  return lazyFetchUsers(url, obj, namespaces.ALL_USERS);
}

export function lazyFetchAllVerifiedUsers(url: string, params?: Object) {
  const obj = params && typeof params === 'object' ? params : null;

  return lazyFetchAllUsers(url, obj);
}

export function lazyFetchUsers(
  url: string,
  params?: Object,
  namespace?: string,
) {
  return (dispatch: any, _: GetState, { get }: Object) => {
    dispatch(lazyFetchUsersRequest(namespace));

    return get(url, params)
      .then(parseResponse)
      .then((json) => {
        const { next, previous, results } = json;
        const normalizedData = normalizeUsers(results);
        const { entities, result } = normalizedData;

        dispatch(patchEntities(entities));
        // $FlowIssue
        dispatch(lazyFetchUsersSuccess(result, next, previous, namespace));
      })
      .catch((e) => {
        dispatch(lazyFetchUsersFailure(e, namespace));

        throw e;
      });
  };
}

export function lazyFetchUsersRequest(namespace?: string) {
  return {
    type: types.LAZY_FETCH_USERS_REQUEST,
    namespace,
  };
}

export function lazyFetchUsersSuccess(
  result: Array<User>,
  next?: string,
  previous?: string,
  namespace?: string,
) {
  return {
    type: types.LAZY_FETCH_USERS_SUCCESS,
    result,
    namespace,
    next,
    previous,
  };
}

export function lazyFetchUsersFailure(exception: Error, namespace?: string) {
  return {
    type: types.LAZY_FETCH_USERS_FAILURE,
    exception,
    namespace,
  };
}

// Reset

export function resetUser() {
  return {
    type: types.RESET_USER,
  };
}

// Fetch user statistics

export function fetchUserStatistics(id: number, eventId: number) {
  return (dispatch: any, _: GetState, { smartGet }: Object) => {
    dispatch(fetchUserStatisticsRequest());

    return smartGet(userStatisticsEndpoint(id, eventId))
      .then((result) => {
        dispatch(fetchUserStatisticsSuccess(id, result));

        return result;
      })
      .catch((exception) => {
        dispatch(fetchUserStatisticsFailure(exception));

        throw exception;
      });
  };
}

export function fetchUserStatisticsRequest() {
  return {
    type: types.FETCH_USER_STATISTICS_REQUEST,
  };
}

export function fetchUserStatisticsSuccess(userId: number, result: number) {
  return {
    type: types.FETCH_USER_STATISTICS_SUCCESS,
    userId,
    result,
  };
}

export function fetchUserStatisticsFailure(exception: Error) {
  return {
    type: types.FETCH_USER_STATISTICS_FAILURE,
    exception,
  };
}

// Bookmark Users

export function bookmarkUsers(url: string, bookmarked: boolean) {
  return (dispatch: any, _: GetState, { post }: Object) => {
    dispatch(bookmarkUsersRequest());

    return post(url, { bookmarked })
      .then(parseResponse)
      .then((json) => {
        const { id, bookmarked } = json;

        dispatch(bookmarkUsersSuccess(id, bookmarked));

        return bookmarked;
      })
      .catch((e) => {
        dispatch(bookmarkUsersFailure(e));

        throw e;
      });
  };
}

export function bookmarkUsersRequest() {
  return {
    type: types.BOOKMARK_USER_REQUEST,
  };
}

export function bookmarkUsersSuccess(id: number, bookmarked: boolean) {
  return {
    type: types.BOOKMARK_USER_SUCCESS,
    id,
    bookmarked,
  };
}

export function bookmarkUsersFailure(exception: Error) {
  return {
    type: types.BOOKMARK_USER_FAILURE,
    exception,
  };
}

// Fetch user ability to create a new topic

export function fetchUserCanCreateTopic(id: number, eventId: number) {
  return (dispatch: any, _: GetState, { smartGet }: Object) => {
    dispatch(unsetNewTopicReady());
    dispatch(fetchUserCanCreateTopicRequest());

    return smartGet(userCanCreateTopicEndpoint(id, eventId))
      .then((result) => {
        dispatch(fetchUserCanCreateTopicSuccess(result));
        dispatch(setNewTopicReady());
        return result;
      })
      .catch((exception) => {
        dispatch(fetchUserCanCreateTopicFailure(exception));
        throw exception;
      });
  };
}

export function fetchUserCanCreateTopicRequest() {
  return {
    type: types.FETCH_USER_CAN_CREATE_TOPIC_REQUEST,
  };
}

export function fetchUserCanCreateTopicSuccess(result: Object) {
  return {
    type: types.FETCH_USER_CAN_CREATE_TOPIC_SUCCESS,
    result,
  };
}

export function fetchUserCanCreateTopicFailure(exception: Error) {
  return {
    type: types.FETCH_USER_CAN_CREATE_TOPIC_FAILURE,
    exception,
  };
}

// Patch user

export function patchUser(
  userId: number | string,
  eventId: string | number,
  data: Object,
) {
  return (dispatch: any, _: GetState, { patch }: Object) => {
    dispatch(patchUserRequest());

    return patch(userEndpoint(userId, eventId), data)
      .then(parseResponse)
      .then((json) => {
        const normalizedData = normalizeUser(json);
        const { entities, result } = normalizedData;

        dispatch(patchEntities(entities));
        dispatch(patchUserSuccess(result));

        return result;
      })
      .catch((e) => {
        dispatch(patchUserFailure(e));

        throw e;
      });
  };
}

export function patchUserRequest() {
  return {
    type: types.PATCH_USER_REQUEST,
  };
}

export function patchUserSuccess(result: number) {
  return {
    type: types.PATCH_USER_SUCCESS,
    result,
  };
}

export function patchUserSignupSuccess() {
  return {
    type: types.PATCH_USER_SIGNUP_SUCCESS,
  };
}

export function patchUserFailure(exception: Error) {
  return {
    type: types.PATCH_USER_FAILURE,
    exception,
  };
}

// Self

export function fetchSelf(eventId: string | number, options?: Object) {
  return fetchUser('me', eventId, options, namespaces.SELF);
}

export function fetchSelfOptimally(eventId: string | number) {
  return fetchSelf(eventId, { optimized: true });
}

export function patchSelf(eventId: string | number, data: Object) {
  return patchUser('me', eventId, data);
}

// Activity

export function fetchUserActivity(
  eventId: number,
  id: number,
  onlyConfirmed?: boolean = false,
  ignoreOwnBraindates?: boolean = false,
  ignoreBraindateId?: number,
  categories?: string[],
) {
  return (dispatch: any, _: GetState, { get }: Object) => {
    dispatch(fetchUserActivityRequest());

    const data = {
      event: eventId,
    };

    if (onlyConfirmed) {
      // $FlowIssue
      data.confirmed = true;
    }

    if (ignoreOwnBraindates) {
      // $FlowIssue
      data['ignore-my-braindates'] = ignoreOwnBraindates;
    }

    if (ignoreBraindateId) {
      // $FlowIssue
      data['ignore-braindate-id'] = ignoreBraindateId;
    }

    if (categories) {
      // $FlowIssue
      data.categories = categories.join(',');
    }

    return get(userActivityEndpoint(id), data)
      .then(parseResponse)
      .then((json) => dispatch(fetchUserActivitySuccess(json, id)))
      .catch((e) => {
        dispatch(fetchUserActivityFailure(e));

        throw e;
      });
  };
}

export function fetchUserActivityRequest() {
  return {
    type: types.FETCH_USER_ACTIVITY_REQUEST,
  };
}

export function fetchUserActivitySuccess(
  result: Array<UserActivity>,
  userId: number,
) {
  return {
    type: types.FETCH_USER_ACTIVITY_SUCCESS,
    result,
    userId,
  };
}

export function fetchUserActivityFailure(exception: Error) {
  return {
    type: types.FETCH_USER_ACTIVITY_FAILURE,
    exception,
  };
}

// Avatar

export function updateUserAvatar(
  id: number,
  file: any,
  cropCenter: Object,
  cropDim: Object,
) {
  const { x, y } = cropCenter;
  const { width, height } = cropDim;

  return (dispatch: any, _: GetState, { post }: Object) => {
    dispatch(updateUserAvatarRequest());

    const data = new FormData();

    data.append('image', file);
    data.append('center_x', x);
    data.append('center_y', y);
    data.append('width', width);
    data.append('height', height);

    return post(userAvatarEndpoint(id), data)
      .then(parseResponse)
      .then((avatar) => dispatch(updateUserAvatarSuccess(avatar, id)))
      .catch((e) => {
        dispatch(updateUserAvatarFailure(e));

        throw e;
      });
  };
}

export function updateUserAvatarRequest() {
  return {
    type: types.UPDATE_AVATAR_REQUEST,
  };
}

export function updateUserAvatarSuccess(result: Object, userId: number) {
  return {
    type: types.UPDATE_AVATAR_SUCCESS,
    result,
    userId,
  };
}

export function updateUserAvatarFailure(exception: Error) {
  return {
    type: types.UPDATE_AVATAR_FAILURE,
    exception,
  };
}

/*
 |------------------------------------------------------------------------------
 | Recommendation
 |------------------------------------------------------------------------------
 */

export function userRecommendTopic(
  receiverUserId: number,
  recommendedTopicUrl: string,
  message: string,
) {
  return (dispatch: any, _: GetState, { post }: Object) =>
    post(userRecommendationTopicEndpoint(receiverUserId), {
      message,
      topic: recommendedTopicUrl,
    })
      .then(parseResponse)
      .catch((e) => {
        throw e;
      });
}

export function userRecommendUser(
  receiverUserId: number,
  recommendedUserUrl: string,
  eventUrl: string,
  message: string,
) {
  return (dispatch: any, _: GetState, { post }: Object) =>
    post(userRecommendationUserEndpoint(receiverUserId), {
      message,
      user: recommendedUserUrl,
      event: eventUrl,
    })
      .then(parseResponse)
      .catch((e) => {
        throw e;
      });
}
